/* eslint-disable node/no-extraneous-import */
/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from 'moment-timezone';
import {
  every,
  get,
  has,
  isArray,
  isEmpty,
  isObject,
  isString,
  lowerCase,
  reduce,
  some,
} from 'lodash';
import {GetMatchingDataFromApiResponseConfigInterface} from '../interfaces/helpers';

export const StringIsNullOrEmptyOrUndefined = (
  string: string | null | undefined
) => {
  return string === '' || string === null || string === undefined;
};

export const IsNullOrEmptyOrUndefined = (value: any): boolean => {
  // Check for null or undefined
  if (value === null || value === undefined) {
    return true;
  }

  // Check for empty strings
  if (typeof value === 'string' && value.trim() === '') {
    return true;
  }

  // Check for empty arrays
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }

  // Check for empty objects
  if (
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  ) {
    return true;
  }

  // Check for NaN
  if (typeof value === 'number' && isNaN(value)) {
    return true;
  }

  // If none of the conditions match, the value is not null, empty, or undefined
  return false;
};

export const GetCurrentSingaporeTimeZone = (): Date => {
  return new Date(moment.tz('Asia/Singapore').format('YYYY-MM-DD HH:mm:ss'));
};

export const stringFormat = (template: string, ...values) => {
  return template.replace(/{(\d+)}/g, (match, number) => {
    return typeof values[number] !== 'undefined' ? values[number] : match;
  });
};

export const parseToJson = (value: any, fallbackValue?: any) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallbackValue || value;
  }
};

/**
 * Get matching data from response, compare with the list parameter
 * @param {*} list List is the config list of api responses
 * @param {*} response Api response
 * @returns
 */
export const getMatchingDataFromResponse = (
  list: GetMatchingDataFromApiResponseConfigInterface[] = [],
  response: any = {}
) => {
  const matchedData = reduce(
    list,
    (
      arr: any[],
      configListValue: GetMatchingDataFromApiResponseConfigInterface
    ) => {
      const listValue = parseToJson(configListValue.apiResponses);

      const matchedResponse = some(listValue, val => {
        return every(Object.keys(val), key => {
          const localVal = parseToJson(get(val, `${key}`));
          const responseValue = get(response, `${key}`);

          let matchedCondition = false;

          const compareType = get(localVal, 'VARIANT_MAP_COMPARE_TYPE');

          if (compareType === 'partialMatch') {
            // if compareType is partialMatch
            // localVal matches the condition when
            // - responseValue is an array and each item in array contains localVal.value
            // - responseValue is not an array and responseValue contains localVal.value
            const localValueToCheckPartialMatch = get(localVal, 'value');
            const responseValueIncludeConditionValue = (
              localResponseValue,
              localConditionValue
            ) =>
              lowerCase(localResponseValue).includes(
                lowerCase(localConditionValue)
              );
            if (isArray(responseValue)) {
              matchedCondition = some(responseValue, localResponseValue =>
                responseValueIncludeConditionValue(
                  localResponseValue,
                  localValueToCheckPartialMatch
                )
              );
            } else {
              matchedCondition = responseValueIncludeConditionValue(
                responseValue,
                localValueToCheckPartialMatch
              );
            }
          } else {
            // if there is no compareType, compare localVal with legacy logic
            // localVal matches the condition when
            // - localVal is equal to responseValue
            // - responseValue is an array and responseValue includes localVal
            const isEqualValue = localVal === responseValue;
            const isArrayContainValue =
              isArray(responseValue) && responseValue.includes(localVal);

            matchedCondition = isEqualValue || isArrayContainValue;
          }

          return matchedCondition;
        });
      });

      // If matchedResponse is true, that means there is matched data, add the listKey into the array
      if (matchedResponse) {
        arr.push(configListValue.message);
      }

      return arr;
    },
    []
  );
  return matchedData;
};

/**
 * Extracts parameter keys from a string in the format {{param}} or %7B%7Bparam%7D%7D.
 * @param {*} str
 * @returns
 */
export const extractParamKeys = (str: string) => {
  // Regular expression to match the unencoded "{{param}}" format
  const regex = /{{(\w+)}}/g;

  // Regular expression to match the encoded "%7B%7Bparam%7D%7D" format
  const encodedRegex = /%7B%7B(\w+)%7D%7D/g;

  // Array to hold the matches
  const matches: any[] = [];

  // Loop through the unencoded matches
  let match;
  while ((match = regex.exec(str)) !== null) {
    // Add the match to the array with the "isEncoded" flag set to false
    matches.push({
      key: match[1],
      isEncoded: false,
    });
  }

  // Loop through the encoded matches
  while ((match = encodedRegex.exec(str)) !== null) {
    // Add the match to the array with the "isEncoded" flag set to true
    matches.push({
      key: match[1],
      isEncoded: true,
    });
  }

  // Return the array of matches
  return matches;
};

/**
 * Replace dynamic hardcoded strings with value from FE\
 * Use case: dynamic tags in Sitecore data
 *
 * **Example usage:**
 * ```
 *  replaceDynamicContent(STRING_ITEM,
 *    [{
 *      string: "{{REPLACE_ME}}",
 *      replace: "NEW_VALUE"
 *    }]
 *  );
 *
 * ```
 * **replacement object supports both array and object formats**
 *
 * @param {str} str                     string to replace
 * @param {object|array} replacements   single/multi replace object
 * @param {object} options               extractAllReplacements: boolean - dynamically replaces all matching objects between str and replacements
 * @returns formatted string value
 */
export const replaceDynamicContent = (str, replacements, options?: any) => {
  const {extractAllReplacements = false} = options || {};

  /**
   * Default replacement internal utility
   * @param {*} paramStr
   * @param {*} replacementData
   * @returns
   */
  const defaultReplaceDynamicContent = (
    paramStr: string,
    replacementData: any
  ) => {
    // clone to localised version
    let localStr = isString(paramStr) ? paramStr || '' : '';

    const getReplacement = replacement => {
      const escapedReplacement = replacement
        .toString()
        .replace(/[\t\n\r]/g, match => {
          switch (match) {
            case '\t':
              return '\\t';
            case '\n':
              return '\\n';
            case '\r':
              return '\\r';
            default:
              return match;
          }
        });
      return escapedReplacement;
    };

    if (isArray(replacementData) && !isEmpty(replacementData)) {
      // loop through all replacement items
      for (let i = 0; i < replacementData.length; i += 1) {
        const {string: strToReplace, replace: replacement} = replacementData[i];

        // throw warning if replace object is missing
        if (!replacement) {
          console.warn(
            `[replaceDynamicContent] Replacement value is empty for ${strToReplace}`
          );
        }

        const escapedReplacement = ![null, undefined].includes(replacement)
          ? getReplacement(replacement)
          : replacement;

        // replace and update local string
        localStr = localStr.replaceAll(strToReplace, escapedReplacement);
      }

      // return final format
      return localStr;
    }
    if (isObject(replacementData) && !isEmpty(replacementData)) {
      const {string: strToReplace, replace: replacement} =
        replacementData as any;

      // escape special characters in replacement text
      const escapedReplacement = ![null, undefined].includes(replacement)
        ? getReplacement(replacement)
        : replacement;

      // replace formatted string
      return localStr.replaceAll(strToReplace || '', escapedReplacement);
    }

    // if replacement is not array or object, return original string
    return localStr;
  };

  /**
   * Define a function that extracts dynamic content and replaces it with the appropriate replacement value
   * @param {*} str
   * @param {*} replacement
   * @returns
   */
  const extractAndReplaceDynamicContent = (paramStr, replacementData) => {
    if (!isObject(replacementData)) {
      return paramStr;
    }

    // Extract the parameter keys from the input string
    const params = extractParamKeys(paramStr);
    // Iterate through the parameters and replace dynamic content
    return params.reduce((string, objKey) => {
      // before returning, check if replacement contains the same key as the current objKey
      if (has(replacementData, objKey.key)) {
        return defaultReplaceDynamicContent(string, {
          string: objKey.isEncoded
            ? encodeURIComponent(`{{${objKey.key}}}`)
            : `{{${objKey.key}}}`,
          replace: extractAndReplaceDynamicContent(
            get(replacementData, objKey.key),
            replacementData
          ),
        });
      }

      // if there are no matches, return the string as normal
      return string;
    }, paramStr);
  };

  return extractAllReplacements
    ? extractAndReplaceDynamicContent(str, replacements)
    : defaultReplaceDynamicContent(str, replacements);
};

export function getPathValue(obj: any, path: string, config: any = {}): any {
  if (!obj || !path) return null;
  const {fallbackValuesByPath = {}, defaultFallbackValueByPath} = config;
  const fallbackValue =
    fallbackValuesByPath?.[path] || defaultFallbackValueByPath;
  const parts = path.split('.').map(p => p.trim());
  return (
    parts.reduce((acc: any, key: string) => {
      if (!acc) return undefined;

      // numeric index: "arr.0"
      if (/^\d+$/.test(key)) {
        return acc?.[Number(key)];
      }

      // common property names (length works on arrays/strings)
      // fallback to property access for everything else
      return acc?.[key];
    }, obj) || fallbackValue
  );
}

export function formatTemplate(
  template: string,
  data: any,
  fallback: any = '',
  config: any = {}
): string {
  if (!template) return fallback;
  return template.replace(/\{([^}]+)\}/g, (_, rawKey) => {
    const key = rawKey.trim();
    const val = getPathValue(data, key, config);
    return val === null ? '' : String(val);
  });
}
